import React, { useContext, useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import ApplicationContext from '../contexts/ApplicationContext';
import { helpdeskAPI } from '../services/api';
import CreateTicketModal from './CreateTicketModal';

const Dashboard = () => {
  const { currentApplication } = useContext(ApplicationContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleTicketCreated = (newTicket) => {
    // Refresh dashboard data
    fetchDashboardData();
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch statistics
      const statsResponse = await helpdeskAPI.getTicketStatistics();
      setStats(statsResponse.data);
      
      // Fetch recent tickets (limit to 5)
      const ticketsResponse = await helpdeskAPI.getTickets({ 
        limit: 5, 
        ordering: '-created_at' 
      });
      setTickets(ticketsResponse.data.results || ticketsResponse.data);
      
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please check if the API server is running.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleTicketClick = (ticketId) => {
    navigate(`/tickets/${ticketId}`);
  };

  if (loading) {
    return (
      <div className="main-content">
        <div className="text-center py-5">
          <Spinner animation="border" role="status" className="me-2" />
          <span>Loading dashboard...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-content">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Dashboard</Alert.Heading>
          <p>{error}</p>
          <hr />
          <p className="mb-0">
            Make sure the helpdesk backend API is reachable from this environment.
          </p>
        </Alert>
      </div>
    );
  }

  return (
    <div className="main-content dashboard-screen">
      <Row className="mb-3 mb-lg-4">
        <Col>
          <h2 className="dashboard-title">
            <i className="fas fa-tachometer-alt me-2"></i>
            Dashboard
            {currentApplication && (
              <span className="text-muted fs-6 ms-2 dashboard-subtitle">
                - {currentApplication.name}
              </span>
            )}
          </h2>
        </Col>
      </Row>

      <Row className="mb-3 mb-lg-4 g-3 dashboard-stats-row">
        <Col xs={6} md={3}>
          <Card className="stats-card h-100">
            <Card.Body className="text-center dashboard-stat-card-body">
              <div className="display-6 text-primary dashboard-stat-icon">
                <i className="fas fa-ticket-alt"></i>
              </div>
              <h3 className="mt-2 mb-1 dashboard-stat-value">{stats?.total || 0}</h3>
              <p className="text-muted mb-0 dashboard-stat-label">Total Tickets</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="stats-card warning h-100">
            <Card.Body className="text-center dashboard-stat-card-body">
              <div className="display-6 text-warning dashboard-stat-icon">
                <i className="fas fa-clock"></i>
              </div>
              <h3 className="mt-2 mb-1 dashboard-stat-value">{stats?.open || 0}</h3>
              <p className="text-muted mb-0 dashboard-stat-label">Open Tickets</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="stats-card success h-100">
            <Card.Body className="text-center dashboard-stat-card-body">
              <div className="display-6 text-success dashboard-stat-icon">
                <i className="fas fa-cog"></i>
              </div>
              <h3 className="mt-2 mb-1 dashboard-stat-value">{stats?.in_progress || 0}</h3>
              <p className="text-muted mb-0 dashboard-stat-label">In Progress</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="stats-card danger h-100">
            <Card.Body className="text-center dashboard-stat-card-body">
              <div className="display-6 text-danger dashboard-stat-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <h3 className="mt-2 mb-1 dashboard-stat-value">{stats?.overdue || 0}</h3>
              <p className="text-muted mb-0 dashboard-stat-label">Overdue</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3">
        <Col lg={8}>
          <Card className="dashboard-panel-card">
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-list me-2"></i>
                Recent Tickets
              </h5>
            </Card.Header>
            <Card.Body>
              {tickets.length > 0 ? (
                <>
                <div className="table-responsive d-none d-md-block">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Ticket</th>
                        <th>Title</th>
                        <th>Status</th>
                        <th>Priority</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.map((ticket) => (
                        <tr 
                          key={ticket.id} 
                          onClick={() => handleTicketClick(ticket.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>
                            <small className="text-muted">#{ticket.ticket_number || ticket.id.slice(0, 8)}</small>
                          </td>
                          <td>
                            <strong>{ticket.title}</strong>
                          </td>
                          <td>
                            <span className={`badge ${getStatusBadgeClass(ticket.status)}`}>
                              {ticket.status.replace('_', ' ').toUpperCase()}
                            </span>
                          </td>
                          <td>
                            <span 
                              className="badge" 
                              style={{ backgroundColor: ticket.priority_color || '#6c757d' }}
                            >
                              {ticket.priority_name || 'Normal'}
                            </span>
                          </td>
                          <td>
                            <small className="text-muted">
                              {new Date(ticket.created_at).toLocaleDateString()}
                            </small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="dashboard-ticket-stack d-md-none">
                  {tickets.map((ticket) => (
                    <button
                      key={ticket.id}
                      type="button"
                      className="dashboard-ticket-card"
                      onClick={() => handleTicketClick(ticket.id)}
                    >
                      <div className="dashboard-ticket-card-top">
                        <span className="dashboard-ticket-number">
                          #{ticket.ticket_number || ticket.id.slice(0, 8)}
                        </span>
                        <span className={`badge ${getStatusBadgeClass(ticket.status)}`}>
                          {ticket.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      <div className="dashboard-ticket-card-title">{ticket.title}</div>
                      <div className="dashboard-ticket-card-meta">
                        <span
                          className="badge"
                          style={{ backgroundColor: ticket.priority_color || '#6c757d' }}
                        >
                          {ticket.priority_name || 'Normal'}
                        </span>
                        <small className="text-muted">
                          {new Date(ticket.created_at).toLocaleDateString()}
                        </small>
                      </div>
                    </button>
                  ))}
                </div>
                </>
              ) : (
                <div className="text-center text-muted py-4 dashboard-empty-state">
                  <i className="fas fa-inbox display-1 mb-3"></i>
                  <p>No recent tickets to display</p>
                  <small>Create your first ticket to get started</small>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4}>
          <Card className="dashboard-panel-card">
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-chart-pie me-2"></i>
                Quick Actions
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2 dashboard-action-grid">
                <button 
                  className="btn btn-primary" 
                  onClick={() => setShowCreateModal(true)}
                >
                  <i className="fas fa-plus me-2"></i>
                  Create New Ticket
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => navigate('/tickets')}
                >
                  <i className="fas fa-search me-2"></i>
                  Search Tickets
                </button>
                <button
                  className="btn btn-outline-warning"
                  onClick={() => navigate('/review-queue')}
                >
                  <i className="fas fa-inbox me-2"></i>
                  Review Queue
                </button>
                <button
                  className="btn btn-outline-info"
                  onClick={() => navigate('/knowledge-base')}
                >
                  <i className="fas fa-book me-2"></i>
                  Knowledge Base
                </button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <CreateTicketModal 
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onTicketCreated={handleTicketCreated}
      />
    </div>
  );
};

// Helper function to get status badge class
const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'open':
      return 'bg-warning text-dark';
    case 'in_progress':
      return 'bg-info';
    case 'resolved':
      return 'bg-success';
    case 'closed':
      return 'bg-secondary';
    case 'pending_customer':
      return 'bg-warning';
    case 'pending_internal':
      return 'bg-primary';
    default:
      return 'bg-secondary';
  }
};

export default Dashboard;
